// Employees: every salesperson's numbers for the chosen period in one
// searchable, sortable table. Leaderboard: the same people ranked on the
// metric management cares about right now.
import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronRight, Search, Trophy } from 'lucide-react';
import { inr, pctText, STATUS_LABEL, STATUS_TONE, type PerfRow } from '../api';
import { Bar, Chip, Empty, Initials, Panel, toneFor } from './ui';

type SortKey = 'name' | 'target' | 'sales' | 'newSales' | 'achievement' | 'conversions' | 'score' | 'incentive';
const value = (r: PerfRow, k: SortKey): number | string => ({
  name: r.name.toLowerCase(), target: r.metrics.target, sales: r.metrics.grossSales, newSales: r.metrics.newSales,
  achievement: r.metrics.achievementPct ?? -1, conversions: r.metrics.conversions, score: r.score ?? -1, incentive: r.incentive,
}[k]);

const gradeTone = (g: string) => (g.startsWith('A') ? 'bg-emerald-50 text-emerald-700' : g.startsWith('B') ? 'bg-blue-50 text-blue-700' : g.startsWith('C') ? 'bg-amber-50 text-amber-700' : g === 'No target' ? 'bg-slate-100 text-slate-500' : 'bg-rose-50 text-rose-700');

export function EmployeesTab({ rows, onOpen }: { rows: PerfRow[]; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [band, setBand] = useState<'all' | 'above' | 'near' | 'below' | 'none'>('all');
  const [sort, setSort] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: 'sales', dir: -1 });
  const bands = {
    above: rows.filter((r) => (r.metrics.achievementPct ?? -1) >= 100).length,
    near: rows.filter((r) => r.metrics.achievementPct !== null && r.metrics.achievementPct >= 80 && r.metrics.achievementPct < 100).length,
    below: rows.filter((r) => r.metrics.achievementPct !== null && r.metrics.achievementPct < 80).length,
    none: rows.filter((r) => r.metrics.achievementPct === null).length,
  };
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows
      .filter((r) => !t || [r.name, r.username, r.employeeCode, r.territory, r.department, r.managerName].some((x) => (x || '').toLowerCase().includes(t)))
      .filter((r) => {
        const a = r.metrics.achievementPct;
        if (band === 'above') return a !== null && a >= 100;
        if (band === 'near') return a !== null && a >= 80 && a < 100;
        if (band === 'below') return a !== null && a < 80;
        if (band === 'none') return a === null;
        return true;
      })
      .sort((a, b) => { const x = value(a, sort.k); const y = value(b, sort.k); return (x < y ? -1 : x > y ? 1 : 0) * sort.dir; });
  }, [rows, q, band, sort]);
  const th = (k: SortKey, label: string, right = false) => (
    <th className={`whitespace-nowrap px-4 py-2.5 ${right ? 'text-right' : ''}`}>
      <button type="button" onClick={() => setSort((s) => ({ k, dir: s.k === k ? (s.dir === 1 ? -1 : 1) : k === 'name' ? 1 : -1 }))} className="inline-flex items-center gap-1 uppercase hover:text-slate-800">
        {label}{sort.k === k && (sort.dir === 1 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );

  if (!rows.length) return <Empty title="No sales employees in this view" text="Clear the filters, or add people to the Sales / Field Executive department." />;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {([['all', 'Everyone', rows.length, 'text-slate-900'], ['above', 'Target achieved', bands.above, 'text-emerald-600'], ['near', '80–99 %', bands.near, 'text-amber-600'], ['below', 'Below 80 %', bands.below, 'text-rose-600'], ['none', 'No target set', bands.none, 'text-slate-400']] as const).map(([k, label, n, tone]) => (
          <button key={k} type="button" onClick={() => setBand(k)} className={`rounded-2xl border bg-white px-4 py-3 text-left transition ${band === k ? 'border-blue-500 ring-2 ring-blue-500/15' : 'border-slate-200/80 hover:border-slate-300'}`}>
            <p className="text-data-xs font-medium text-slate-500">{label}</p>
            <p className={`text-xl font-bold ${tone}`}>{n}</p>
          </button>
        ))}
      </div>

      <Panel pad={false} title="Employee performance" subtitle="Click anyone to open their full profile"
        right={(
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, territory…" className="w-60 rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-[13px] outline-none focus:border-blue-500" />
          </div>
        )}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="bg-slate-50 text-left text-data-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">#</th>
              {th('name', 'Employee')}
              {th('target', 'Target', true)}
              {th('sales', 'Sales', true)}
              {th('newSales', 'New / Existing', true)}
              {th('achievement', 'Achievement')}
              {th('conversions', 'Conversions', true)}
              {th('score', 'Score')}
              {th('incentive', 'Incentive', true)}
              <th />
            </tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.employeeId} onClick={() => onOpen(r.employeeId)} className="cursor-pointer border-t border-slate-100 hover:bg-blue-50/40">
                  <td className="px-4 py-3 text-xs font-semibold text-slate-400">{r.rank}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Initials name={r.name} size={34} />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">{r.name}</p>
                        <p className="truncate text-data-xs text-slate-500">{[r.employeeCode, r.designation || r.department, r.territory].filter(Boolean).join(' · ')}{r.managerName ? ` · reports to ${r.managerName}` : ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">{r.metrics.target ? inr(r.metrics.target) : <span className="text-data-xs text-slate-400">Not set</span>}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">{inr(r.metrics.grossSales)}</td>
                  <td className="px-4 py-3 text-right text-[12px] tabular-nums"><span className="text-emerald-700">{inr(r.metrics.newSales)}</span><span className="text-slate-300"> / </span><span className="text-blue-700">{inr(r.metrics.existingSales)}</span></td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><span className={`w-12 font-semibold tabular-nums ${toneFor(r.metrics.achievementPct).text}`}>{pctText(r.metrics.achievementPct)}</span><Bar pct={r.metrics.achievementPct} /></div></td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.metrics.conversions}<span className="ml-1 text-data-xs text-slate-400">({r.metrics.newCustomers} new)</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.score === null ? <Chip className={gradeTone('No target')}>No target</Chip> : <span className="flex items-center gap-1.5"><b className="tabular-nums">{r.score}</b><Chip className={gradeTone(r.grade)}>{r.grade}</Chip></span>}</td>
                  <td className="px-4 py-3 text-right">
                    <p className="font-semibold tabular-nums">{inr(r.incentive)}</p>
                    {r.incentiveStatus && <Chip className={STATUS_TONE[r.incentiveStatus] || ''}>{STATUS_LABEL[r.incentiveStatus] || r.incentiveStatus}</Chip>}
                  </td>
                  <td className="pr-3 text-slate-300"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-slate-400">Nobody matches.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

const METRICS: Array<{ k: string; label: string; show: (r: PerfRow) => string; num: (r: PerfRow) => number }> = [
  { k: 'score', label: 'Overall score', show: (r) => (r.score === null ? 'No target' : `${r.score} · ${r.grade}`), num: (r) => r.score ?? -1 },
  { k: 'sales', label: 'Highest sales', show: (r) => inr(r.metrics.grossSales), num: (r) => r.metrics.grossSales },
  { k: 'achievement', label: 'Target achievement', show: (r) => pctText(r.metrics.achievementPct), num: (r) => r.metrics.achievementPct ?? -1 },
  { k: 'newCustomers', label: 'New customers', show: (r) => String(r.metrics.newCustomers), num: (r) => r.metrics.newCustomers },
  { k: 'conversions', label: 'Conversions', show: (r) => String(r.metrics.conversions), num: (r) => r.metrics.conversions },
  { k: 'incentive', label: 'Incentive earned', show: (r) => inr(r.incentive), num: (r) => r.incentive },
];

export function LeaderboardTab({ rows, metric, onMetric, onOpen, seesAll }: {
  rows: PerfRow[]; metric: string; onMetric: (m: string) => void; onOpen: (id: string) => void; seesAll: boolean;
}) {
  const m = METRICS.find((x) => x.k === metric) || METRICS[0];
  const ranked = [...rows].sort((a, b) => m.num(b) - m.num(a) || b.metrics.grossSales - a.metrics.grossSales);
  const top = ranked.filter((r) => m.num(r) > 0).slice(0, 3);
  const max = Math.max(1, ...ranked.map((r) => m.num(r)));
  const order = [1, 0, 2].filter((i) => top[i]);
  const heights = ['h-28', 'h-20', 'h-14'];
  const medal = ['🥇', '🥈', '🥉'];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        {METRICS.map((x) => (
          <button key={x.k} type="button" onClick={() => onMetric(x.k)}
            className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition ${m.k === x.k ? 'bg-blue-600 text-white shadow-sm' : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}>{x.label}</button>
        ))}
      </div>

      {top.length > 0 && seesAll && (
        <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50 px-4 pt-8">
          <div className="mx-auto flex max-w-3xl items-end justify-center gap-4">
            {order.map((i) => {
              const r = top[i];
              return (
                <button key={r.employeeId} type="button" onClick={() => onOpen(r.employeeId)} className="flex w-1/3 max-w-[220px] flex-col items-center">
                  <span className="text-3xl">{medal[i]}</span>
                  <Initials name={r.name} size={i === 0 ? 60 : 50} tone={['#d97706', '#64748b', '#c2410c'][i]} />
                  <p className="mt-2 max-w-full truncate text-sm font-bold text-slate-900">{r.name}</p>
                  <p className="text-[12px] font-semibold text-blue-700">{m.show(r)}</p>
                  <div className={`mt-3 w-full rounded-t-xl ${heights[i]} ${['bg-gradient-to-b from-amber-300 to-amber-400', 'bg-gradient-to-b from-slate-200 to-slate-300', 'bg-gradient-to-b from-orange-200 to-orange-300'][i]} flex items-start justify-center pt-2 text-lg font-black text-white/90`}>{i + 1}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Panel title={`Ranking — ${m.label.toLowerCase()}`} icon={Trophy} pad={false}>
        {ranked.length === 0 ? <p className="p-8 text-center text-sm text-slate-400">Nobody to rank.</p> : (
          <ul>
            {ranked.map((r, i) => (
              <li key={r.employeeId}>
                <button type="button" onClick={() => onOpen(r.employeeId)} className="flex w-full items-center gap-4 border-t border-slate-100 px-5 py-3 text-left first:border-t-0 hover:bg-blue-50/40">
                  <span className={`w-7 text-center text-sm font-bold ${i < 3 && m.num(r) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{i + 1}</span>
                  <Initials name={r.name} />
                  <div className="w-48 min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{r.name}</p>
                    <p className="truncate text-data-xs text-slate-500">{r.territory || r.department}</p>
                  </div>
                  <div className="hidden flex-1 sm:block">
                    <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: `${Math.max(0, (m.num(r) / max) * 100)}%` }} /></div>
                  </div>
                  <span className="w-32 text-right text-sm font-bold tabular-nums text-slate-900">{m.show(r)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {m.k === 'score' && <p className="text-center text-data-xs text-slate-400">The score mixes target achievement, conversion, new customers, retention and follow-up activity (weights under Incentive Rules). People without a target are not scored.</p>}
    </div>
  );
}
